import RoundMiniIcon from '@components/atoms/RoundMiniIcon/RoundMiniIcon'
import config from '@config'
import {
  actionResetCompositeUpload,
  actionSetSerializedFile,
  actionSetStepsFinishedManually,
} from '@store/reducers/app'
import {
  convertFileToSerializable,
  getFileByDropEvent,
  getFileByInputUploadEvent,
  validateFile,
  validateUploadImageDimensions,
} from '@utils/file'
import { useEffect } from 'react'
import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getSujetDimensionsByCoordinates } from '@utils/sujetHelpers'
import LightBox from '@components/molecules/LightBox'
import Translate from '@components/atoms/Translate'
import Button from '@components/molecules/Button'
import DropZoneUi from './DropZoneUi/DropZoneUi'
import styles from './Upload.module.scss'
import UploadErrorLb from './UploadErrorLb/UploadErrorLb'

const Upload = () => {
  const inputRef = useRef(null)

  const { serializedFile, selectedSujet } = useSelector((state) => state.app)
  const dispatch = useDispatch()
  const sujetDimensions = getSujetDimensionsByCoordinates(
    selectedSujet?.images?.day?.coordinates
  )

  const saveFileContent = async (file) => {
    const serialized = await convertFileToSerializable(file)

    if (file.type !== 'application/pdf') {
      const saveResult = await validateUploadImageDimensions(
        serialized,
        sujetDimensions
      )

      setShowUploadWarning(!saveResult?.success)
    }
    dispatch(actionSetSerializedFile(serialized))
  }
  const resetStepsFinished = () => {
    dispatch(actionSetStepsFinishedManually(0))
  }

  const [validationError, setValidationError] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [showUploadWarning, setShowUploadWarning] = useState(false)

  const onDrop = (e) => {
    saveFileContent(getFileByDropEvent(e))
    e.preventDefault()
  }

  const onDragOver = (e) => {
    e.preventDefault()
  }

  const triggerOpenFileDialog = () => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }
  const onFileInputChange = (e) => {
    saveFileContent(getFileByInputUploadEvent(e))
  }

  useEffect(() => {
    if (serializedFile) {
      const [result, meta] = validateFile(serializedFile)
      setValidationError(result === true ? false : { error: result, meta })
      setIsValid(result === true)
      if (result !== true) {
        resetStepsFinished()
      }
    }
  }, [serializedFile])

  const reset = () => {
    saveFileContent(false)
    dispatch(actionResetCompositeUpload())
    setValidationError(false)
    setIsValid(false)
    resetStepsFinished()
  }

  return (
    <div className={styles.base}>
      {showUploadWarning && (
        <LightBox
          title={<Translate str="steps.upload.warning.title" />}
          body={<Translate str="steps.upload.warning.text" />}
          buttons={
            <div className={styles.closeButtons}>
              <Button
                bg="dark"
                onClick={() => {
                  setShowUploadWarning(false)
                  triggerOpenFileDialog()
                }}
              >
                <Translate str="steps.upload.warning.cta" />
              </Button>
            </div>
          }
          onClose={() => setShowUploadWarning(false)}
        />
      )}

      {validationError && (
        <UploadErrorLb
          errorType={validationError.error}
          meta={validationError.meta}
          onClose={() => setValidationError(false)}
          onRetry={triggerOpenFileDialog}
        />
      )}
      {((!isValid || !serializedFile) && (
        <div
          className={styles.dropZone}
          onClick={triggerOpenFileDialog}
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <DropZoneUi />
        </div>
      )) || (
        <div className="o-grid">
          <div className="o-grid__col u-4/6@md u-push-1/6@md">
            <div className={styles.preview}>
              <div className={styles.reset} onClick={reset}>
                <RoundMiniIcon bg="grey" color="white" icon="close" />
              </div>

              {(serializedFile.type === 'application/pdf' && (
                <div className={styles.pdfPreview}>
                  <img
                    id="uploadPreview"
                    src={config.pdfImage}
                    className={styles.pdfPreview}
                  />
                  <span>{serializedFile.name}</span>
                </div>
              )) || <img id="uploadPreview" src={serializedFile.content} />}
            </div>
          </div>
        </div>
      )}
      <div className={styles.hiddenFileInput}>
        <input ref={inputRef} onChange={onFileInputChange} type="file" />
      </div>
    </div>
  )
}
export default Upload
